import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Navigate, RouterProvider, createBrowserRouter, useParams } from "react-router-dom";
import { Dashboard } from "./routes/Dashboard.js";
import { NewLaunchVideo } from "./launch/NewLaunchVideo.js";
import { NewProject } from "./routes/NewProject.js";
import { NewProjectChooser } from "./routes/NewProjectChooser.js";
import { ProjectOverview } from "./routes/ProjectOverview.js";
import { Editor } from "./routes/Editor.js";
import { CutReview } from "./routes/CutReview.js";
import { ExportRoute } from "./routes/ExportRoute.js";
import { Views } from "./routes/Views.js";
import { Steps } from "./routes/Steps.js";
import { Settings } from "./routes/Settings.js";
import { Welcome } from "./routes/Welcome.js";
import { AdminCustomers } from "./admin/AdminCustomers.js";
import { AdminTeam } from "./admin/AdminTeam.js";
// The card library: the editor and the renderer import the same module, so a
// preview cannot show a face the encoder does not have.
import "@veyso/remotion/fonts";
// The interface's own face, kept separate because it is the app's business
// rather than the card library's — nothing in a video is set in it.
//
// The variable build rather than the static one. The design asks for weights of
// 550 and 650, which no set of fixed cuts can produce: CSS matching rounds them
// up to the next real weight, so a deliberate half-step was silently landing on
// semibold and bold. A 300–700 axis hits them exactly.
import "@fontsource-variable/space-grotesk";
import { CookieBanner } from "./components/CookieBanner.js";
import { Gate } from "./auth/Gate.js";
import { api } from "./api.js";
import { languagesPageTarget } from "./languages/languages.js";
import "./lib/posthog.js";
import "./styles.css";

/** Preserves the project id when forwarding the retired /script route. */
const ScriptRedirect: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  return <Navigate to={`/projects/${id}/editor`} replace />;
};

/**
 * The languages page is gone; old links go where the languages now live:
 * a product demo's editor with its Languages panel open, or a launch video's
 * studio. Nothing is drawn until the project's kind is known; a project that
 * cannot be read goes to the editor, which reports its own errors.
 */
const LanguagesRedirect: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const [kind, setKind] = useState<"demo" | "launch" | null>(null);
  useEffect(() => {
    let current = true;
    void api
      .getProject(id)
      .then((project) => {
        if (current) setKind(project.kind);
      })
      .catch(() => {
        if (current) setKind("demo");
      });
    return () => {
      current = false;
    };
  }, [id]);
  return kind ? <Navigate to={languagesPageTarget(id, kind)} replace /> : null;
};

const router = createBrowserRouter([
  { path: "/", element: <Navigate to="/dashboard" replace /> },
  { path: "/dashboard", element: <Dashboard /> },
  { path: "/settings", element: <Settings /> },
  { path: "/welcome", element: <Welcome /> },
  // `/projects` and the dashboard show the same list; one canonical URL for it.
  { path: "/projects", element: <Navigate to="/dashboard" replace /> },
  { path: "/projects/new", element: <NewProjectChooser /> },
  { path: "/projects/new/demo", element: <NewProject /> },
  { path: "/projects/new/launch", element: <NewLaunchVideo /> },
  { path: "/projects/:id", element: <ProjectOverview /> },
  // The script now lives inside the editor; keep the old URL working.
  { path: "/projects/:id/script", element: <ScriptRedirect /> },
  { path: "/projects/:id/editor", element: <Editor /> },
  { path: "/projects/:id/cut", element: <CutReview /> },
  { path: "/projects/:id/render", element: <ExportRoute /> },
  { path: "/projects/:id/views", element: <Views /> },
  { path: "/projects/:id/steps", element: <Steps /> },
  { path: "/projects/:id/languages", element: <LanguagesRedirect /> },
  // Shown to everybody; the API answers 404 to anybody who is not an admin, and the page says "Page not found".
  { path: "/admin", element: <AdminCustomers /> },
  { path: "/admin/teams/:orgId", element: <AdminTeam /> },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
]);

/**
 * Reloads a tab whose lazy chunk the last deploy deleted.
 *
 * Chunk names carry a content hash and `/static/` only serves the current
 * build, so a tab opened before a deploy asks for a file that is gone. Only a
 * reload gets it the new names. At most once a minute, so a chunk that is
 * missing for some other reason shows its error instead of looping the tab.
 * The event is not cancelled: the import still rejects, and its caller handles
 * that while the page reloads.
 */
const RELOADED_AT_KEY = "veyso:stale-chunk-reload";
window.addEventListener("vite:preloadError", () => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOADED_AT_KEY));
    if (Date.now() - last < 60_000) return;
    window.sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    // Without session storage there is no loop guard, so no reload either.
    return;
  }
  window.location.reload();
});

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element");

createRoot(container).render(
  <React.StrictMode>
    {/* Outside the router: every route is behind a session, and a login
        redirect that depended on which route you happened to land on would let
        a deep link through. */}
    <Gate>
      <RouterProvider router={router} />
    </Gate>
    {/* Outside the Gate as well as the router: the question is about this
        browser, not about this session, and somebody who has not signed in is
        still being counted or not counted. */}
    <CookieBanner />
  </React.StrictMode>,
);
